import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import { getServerSideLocale } from '@telus/next-locale-utilities'

import '@telus-uds/palette-allium/build/web/fonts/fonts-cdn.css'

import sitebuilderConfig from '../../../sitebuilder.config'

import { handleExcludesQueryParam, ValidExcludes } from '@/utils/handleExcludesQueryParam'

import { GenericPage, GenericPageProps } from '../../siteBuilder/components/GenericPage'
import { Language, Locale, Province, removeLocaleFromSlug } from '../../siteBuilder/locale'
import { getGenericPageTemplateFromSlug } from '../../siteBuilder/utils/getGenericPageTemplateFromSlug'
import resolveStructuredData from '../../siteBuilder/components/Head/StructuredDataFactory/resolveStructuredData'
import pruneStructuredDataPayloads from '../../siteBuilder/components/Head/StructuredDataFactory/pruneStructuredDataPayloads'
import { ISiteBuilderEntry } from '@/siteBuilder/utils/types/ISiteBuilderEntryTypes'

import { handleRedirect } from '../../siteBuilder/utils/handleRedirect'
import { getLineOfBusiness } from '../../siteBuilder/lineOfBusiness/lineOfBusiness'
import { getContentfulLocale } from '../../siteBuilder/locale/getContentfulLocale'
import { Viewport } from '../../siteBuilder/components/Theme/Theme'

import {
  setPersonalizationRequestCookies,
  setPersonalizationResponseCookies,
} from '../../siteBuilder/personalization/personalization'

import {
  TypeAbTestRedirectWithoutUnresolvableLinksResponse,
  TypeGenericPageTemplateFields,
  TypeGenericPageTemplateWithoutUnresolvableLinksResponse,
} from '../../../contentful-types'
import logger from '../../logger/logger'
import { handleAuth } from '../../siteBuilder/utils/auth/handleAuth'
import { stripTimelineParam } from '../../siteBuilder/utils/stripTimelineParam'
import { resolveTimelinePreviewFromCtx, TimelinePreviewOption } from '../../siteBuilder/utils/toTimelinePreviewOption'
import { TimelinePreviewError } from '../../siteBuilder/components/TimelinePreviewError/TimelinePreviewError'

// Function to detect viewport from User-Agent (server-side estimation)
const getInitialViewportFromUserAgent = (userAgent?: string): Viewport => {
  if (!userAgent) return 'lg' // Default fallback

  // Mobile devices - typically smaller screens
  if (/Mobile|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    return 'xs'
  }

  // Tablet devices - medium screens
  if (/iPad|Tablet/i.test(userAgent)) {
    return 'md'
  }

  // Desktop - assume large screen by default
  return 'xl'
}

type TelusPageProps = Omit<GenericPageProps, 'genericPageTemplate'> & {
  genericPageTemplate?: GenericPageProps['genericPageTemplate']
  timelinePreviewError?: boolean
}

export default function TelusPage(props: TelusPageProps) {
  if (props.timelinePreviewError) {
    return <TimelinePreviewError />
  }
  if (!props.genericPageTemplate) {
    return null
  }

  return (
    <GenericPage
      genericPageTemplate={props.genericPageTemplate}
      jsonLd={props.jsonLd}
      locale={props.locale}
      excludes={props.excludes}
      analyticsScripts={props.analyticsScripts}
    />
  )
}

const getGenericPageTemplate = async (
  slug: string,
  ctx: GetServerSidePropsContext,
  locale: string,
  correlationId: string,
  timelinePreviewOption: TimelinePreviewOption | null
): Promise<ISiteBuilderEntry<TypeGenericPageTemplateWithoutUnresolvableLinksResponse['fields']>> => {
  try {
    const forceContentfulFallbackCache = (ctx.req.headers['force-contentful-fallback-cache'] as string) || 'false'

    const genericPageTemplate = await getGenericPageTemplateFromSlug(
      slug === '' ? '/' : slug,
      sitebuilderConfig.space,
      ctx.req.cookies,
      locale,
      correlationId,
      forceContentfulFallbackCache,
      'genericPageTemplate',
      timelinePreviewOption
    )
    return genericPageTemplate as unknown as ISiteBuilderEntry<
      TypeGenericPageTemplateWithoutUnresolvableLinksResponse['fields']
    >
  } catch (error) {
    if (error instanceof Error) {
      // TODO: does this make sense throwing the same thrown error
      throw new Error(error.message)
    }
  }
}

/**
 * The main SSR function
 * Gets the data from contentful using the incoming slug
 */
export const getServerSideProps = async (
  ctx: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<TelusPageProps>> => {
  const { params } = ctx
  const slug = removeLocaleFromSlug(params?.slug)
  let correlationId = ''
  // correlation id is found in different places depending on if nextjs is run via npm run dev or npm start
  // Not sure why :(
  if (ctx.req.headers['correlation-id']) {
    // Required in dev
    correlationId = ctx.req.headers['correlation-id'] as string
  } else if (ctx.res?.getHeader('correlation-id')) {
    // Required in prod
    correlationId = ctx.res?.getHeader('correlation-id') as string
  }

  const serverSideLocale = getServerSideLocale(ctx)
  const locale: Locale = {
    language: serverSideLocale.lang as Language,
    province: serverSideLocale.prov as Province,
    country: 'CA',
  }

  const contentfulLocale = getContentfulLocale(locale)

  ctx.req.cookies = setPersonalizationRequestCookies(ctx)

  const timelinePreviewOption = resolveTimelinePreviewFromCtx(ctx, { correlationId, slug })
  const timelinePreview = !!timelinePreviewOption

  let genericPageTemplate: ISiteBuilderEntry<TypeGenericPageTemplateWithoutUnresolvableLinksResponse['fields']>
  try {
    genericPageTemplate = await getGenericPageTemplate(
      slug,
      ctx,
      contentfulLocale,
      correlationId,
      timelinePreviewOption
    )
    logger.debug({
      message: timelinePreview
        ? 'Retrieved generic page template from Contentful Timeline preview'
        : 'Retrieved generic page template from Stitch API',
      application: 'SB2',
      env: process.env.APP_ENV,
      correlationId,
      slug,
      locale: contentfulLocale,
      timelinePreview,
    })
  } catch (error) {
    logger.error({
      message: 'Failed to fetch generic page template',
      env: process.env.APP_ENV,
      error,
      correlationId,
      slug,
      requestUrl: stripTimelineParam(ctx.req.url),
      timelinePreview,
    })
    if (timelinePreview && process.env.APP_ENV !== 'production') {
      // Render the diagnostic page inline at the original URL. The APP_ENV check is defence in
      // depth — `timelinePreview` is already false in production because resolveTimelinePreviewFromCtx
      // gates the whole timeline path off there — but the explicit guard here ensures end users
      // can never see this page if that upstream gate is ever refactored.
      // `excludes: {}` and `analyticsScripts: []` satisfy _app.tsx's prop access path.
      return {
        props: {
          timelinePreviewError: true,
          locale,
          brand: 'telus' as const,
          excludes: {},
          analyticsScripts: [],
        },
      }
    }
    return {
      notFound: true,
    }
  }

  if (!genericPageTemplate || !genericPageTemplate.fields) {
    return {
      notFound: true,
    }
  }

  if (genericPageTemplate.fields.isAuthRequired) {
    const result = await handleAuth(ctx)
    if (!result.isAuthenticated) {
      return {
        redirect: {
          destination: result.unauthenticatedRedirectUrl,
          permanent: false,
        },
      }
    }
  }

  // Handle redirect
  if (genericPageTemplate.fields.redirect) {
    // Check for region based redirect
    if (
      genericPageTemplate.fields.regions &&
      genericPageTemplate.fields.regions.length > 0 &&
      Boolean(genericPageTemplate.fields.redirectUnsupportedRegions)
    ) {
      // Handle region based redirects - Does the users current region cookie match one of the available regions
      if (!genericPageTemplate.fields.regions.includes(locale.province as Lowercase<Province>)) {
        return {
          redirect: handleRedirect(genericPageTemplate.fields.redirect.fields),
        }
      }
    } else {
      return {
        redirect: handleRedirect(genericPageTemplate.fields.redirect.fields),
      }
    }
  }

  // Try to find an abRedirect Block on the page
  // abTestRedirect is not part of the genericPageTemplate's block content type union,
  // but may be present at runtime via dynamic content configuration
  const abRedirectBlock = genericPageTemplate.fields.blocks.find(
    (block) => (block?.sys?.contentType?.sys?.id as string) === 'abTestRedirect'
  ) as TypeAbTestRedirectWithoutUnresolvableLinksResponse | undefined

  // Handle A/B Test Redirect
  if (abRedirectBlock) {
    const pageRedirectionUrl = abRedirectBlock.fields.url
    return {
      redirect: {
        destination: pageRedirectionUrl,
        permanent: false,
        basePath: false,
      },
    }
  }

  // Handle personalization — mboxResponses is injected by the stitch proxy, not part of the Contentful schema
  const { mboxResponses } = genericPageTemplate.fields as Record<string, any>
  const setCookieHeaders = setPersonalizationResponseCookies(mboxResponses)
  if (setCookieHeaders.length > 0) {
    ctx.res.setHeader('Set-Cookie', setCookieHeaders)
  }

  // Check the exclude query param to see if we should exclude any functionality
  // Supported excludes are 'ge' and 'analytics'
  // They may be passed together as a comma separated list
  // Ex. ?exclude=ge,analytics
  let excludes: Partial<Record<ValidExcludes, boolean>> = null
  if (process.env.APP_ENV !== 'production') {
    excludes = handleExcludesQueryParam(ctx.query?.excludes as string)
  }

  // Detect initial viewport from User-Agent
  const userAgent = ctx.req.headers['user-agent']
  const initialViewport = getInitialViewportFromUserAgent(userAgent)

  // Resolve structured data here (GCS-sourced by slug, or the Contentful
  // override), then strip any raw payloads from the tree so they aren't
  // duplicated into __NEXT_DATA__ — the JSON-LD is emitted once in <head> and
  // the client doesn't need the source to hydrate. The prune keeps the override
  // field and any residual schemaJson out of the props. See docs/adr/0001.
  // The Redis document keys locales by language-country (`en-CA`), not the
  // province-specific Contentful locale.
  const structuredDataLocale = `${locale.language}-${locale.country}`
  // TODO: product/offers data for PEP pages, provided server-side by the
  // mobility team (Ryan et al.). Until it's wired, this is undefined and the
  // Product provider emits nothing (FAQ behavior is unchanged). See docs/adr/0001.
  const productData = undefined
  const jsonLd =
    (await resolveStructuredData(genericPageTemplate.fields, slug, structuredDataLocale, productData)) ?? null
  pruneStructuredDataPayloads(genericPageTemplate)

  return {
    props: {
      genericPageTemplate,
      jsonLd,
      locale,
      excludes,
      analyticsScripts: getLineOfBusiness(slug).analyticsScripts,
      brand: 'telus',
      initialViewport,
    },
  }
}
